import getSupplierCategoriesByProductCategoryApi from '@/api/get/company/sales-flow/get-supplier-categories-by-product-category';
import getSupplierProductsByProductCategoryApi from '@/api/get/company/sales-flow/get-supplier-products-by-product-category';
import Loader from '@/components/loader/loader-component';
import { useQuery } from '@/hooks/use-api';
import { useCallback, useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_CONTEXT } from '@/utils/context';
import SalesWrapper from '../../sales-flow-manager-style';
import CategoriesSelector from './categories-filter';
import ProductFilterResults from './product-filter-results';
import Styled from './select-product-style';

// THIRD STEP
/**
 * Select a product
 * Parent of the filters on the left and product results on the right
 * @returns {JSX.Element}
 */
const SelectProduct = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { selectedSupplier, selectedProductCategory } = state;
  const [selectedCategories, setSelectedCategories] = useState([]);
  const { setShowSideNav } = useContext(APP_CONTEXT);

  const { data: productData, loading: productsLoading } = useQuery(
    getSupplierProductsByProductCategoryApi,
    selectedSupplier.id,
    selectedProductCategory.id,
  );
  const { data: categoriesData, loading: categoriesLoading } = useQuery(
    getSupplierCategoriesByProductCategoryApi,
    selectedSupplier.id,
    selectedProductCategory.id,
  );

  const [loading, setLoading] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState(productData);

  const onFilterSelectorClick = (id) => {
    const temp = [...selectedCategories];
    const index = temp.indexOf(id);
    if (index > -1) {
      temp.splice(index, 1);
    } else {
      temp.push(id);
    }
    setSelectedCategories(temp);
  };

  const goToConfiguration = (product) => {
    navigate('/shop/sales/configuration', {
      state: {
        selectedSupplier,
        selectedProductCategory,
        selectedProduct: product,
      },
    });
  };

  const handleSelectProduct = (product) => {
    goToConfiguration(product);
  };

  const removeFromSelectedCategories = (id) => {
    const temp = JSON.parse(JSON.stringify(selectedCategories));
    const index = temp.indexOf(id);
    if (index > -1) {
      temp.splice(index, 1);
    }
    setSelectedCategories(temp);
  };

  const filterProductsByCategories = useCallback(() => {
    if (selectedCategories.length > 0) {
      let flatCategories = [];
      categoriesData?.forEach((element) => {
        flatCategories = [...flatCategories, ...element.children];
      });

      const flatSelectedCategories = flatCategories
        ?.filter((o) => selectedCategories.includes(o.id))
        .map((o) => o.id);

      return productData?.filter((product) =>
        flatSelectedCategories.every((cat) =>
          product.categories?.includes(cat),
        ),
      );
    }
    return productData;
  }, [selectedCategories, productData, categoriesData]);

  const clearAllFilters = () => {
    setSelectedCategories([]);
  };

  useEffect(() => {
    setShowSideNav(false);
    return () => setShowSideNav(true);
  }, [setShowSideNav]);

  useEffect(() => {
    setLoading(true);
    const newProducts = filterProductsByCategories();

    setFilteredProducts(newProducts);
    setLoading(false);
  }, [productData, selectedCategories, filterProductsByCategories]);

  return (
    <SalesWrapper>
      <Styled.Container>
        <Styled.FilterContainer>
          {categoriesLoading ? (
            <Loader loading />
          ) : (
            <CategoriesSelector
              data={categoriesData}
              activeFilters={selectedCategories}
              handleRemoveSelectedItemFromFilter={removeFromSelectedCategories}
              handleSelectorClick={onFilterSelectorClick}
              handleClearAll={clearAllFilters}
            />
          )}
        </Styled.FilterContainer>
        <Styled.ResultsContainer>
          {productsLoading || loading ? (
            <Loader loading />
          ) : (
            <ProductFilterResults
              data={filteredProducts}
              selectProduct={handleSelectProduct}
            />
          )}
        </Styled.ResultsContainer>
      </Styled.Container>
    </SalesWrapper>
  );
};

export default SelectProduct;
